// Client-side preview builder for the bulk-import modal. Parses raw text
// (JSON or CSV), validates rows against the same shape the server enforces
// in `server/mfg-domain/bulkImportValidator.ts`, and partitions matchable
// rows into create vs update buckets using normalized name matching.
//
// The server is the source of truth for the actual import — this module is
// a UX courtesy that gives the operator a green/red preview before they
// commit. The matching policy here mirrors `server/mfg-domain/materialNameMatch.ts`
// (lower + trim + collapsed whitespace).
import { parseBulkImport, type BulkImportFormat } from "./bulkImportParser";
import type { MfgBulkImportRow, MfgMaterial } from "./types";

export const REQUIRED_KEYS = ["name", "category"] as const;
export const OPTIONAL_KEYS = [
  "unit",
  "shipping_unit",
  "shipping_unit_weight",
  "pieces_per_shipping_unit",
  "code",
  "qty",
  "price",
  "vendor",
  "notes",
  "is_active",
] as const;
export const RECOGNIZED_KEYS = new Set<string>([...REQUIRED_KEYS, ...OPTIONAL_KEYS]);

export type ParsedPreviewRow = MfgBulkImportRow & {
  idx: number;
  seedsCostEntry: boolean;
};

export type ClientPreview =
  | { kind: "empty" }
  | { kind: "parse-error"; message: string }
  | { kind: "first-row-error"; missing: string[]; format: BulkImportFormat }
  | {
      kind: "ok";
      format: BulkImportFormat;
      total: number;
      validRows: ParsedPreviewRow[];
      skipped: Array<{ idx: number; missing: string[] }>;
      ignoredKeys: string[];
      seededCostEntries: number;
      /** Rows whose name matches an existing material (will UPDATE on import). */
      willUpdate: Array<{ idx: number; row: ParsedPreviewRow; existing: MfgMaterial }>;
      /** Rows whose name has no existing match (will CREATE on import). */
      willCreate: ParsedPreviewRow[];
    };

function parseNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") return parseFloat(v);
  return NaN;
}

function parseBoolean(v: unknown): boolean | undefined {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "yes" || s === "1") return true;
    if (s === "false" || s === "no" || s === "0") return false;
  }
  return undefined;
}

/** Mirrors server-side `normalizeMaterialName`. Keep in sync. */
function normalizeName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

export function buildBulkImportPreview(
  rawText: string,
  existingMaterials: ReadonlyArray<MfgMaterial>
): ClientPreview {
  if (!rawText.trim()) return { kind: "empty" };
  const parsed = parseBulkImport(rawText);
  if (!parsed.ok) return { kind: "parse-error", message: parsed.error };
  if (parsed.rows.length === 0) {
    return { kind: "parse-error", message: "No rows found in input." };
  }

  const firstKeys = new Set(Object.keys(parsed.rows[0]));
  const firstRowMissing = REQUIRED_KEYS.filter((k) => !firstKeys.has(k));
  if (firstRowMissing.length > 0) {
    return { kind: "first-row-error", missing: firstRowMissing, format: parsed.format };
  }

  // Build a normalized-name index of currently-loaded materials. The list
  // shown to the operator is single-plant (the inventory panel filters that
  // way already), so a flat key by name is sufficient for preview.
  const nameIndex = new Map<string, MfgMaterial>();
  for (const m of existingMaterials) {
    const k = normalizeName(m.name);
    if (!nameIndex.has(k)) nameIndex.set(k, m);
  }

  const validRows: ParsedPreviewRow[] = [];
  const skipped: Array<{ idx: number; missing: string[] }> = [];
  const ignoredKeys = new Set<string>();
  const willUpdate: Array<{ idx: number; row: ParsedPreviewRow; existing: MfgMaterial }> = [];
  const willCreate: ParsedPreviewRow[] = [];
  let seededCostEntries = 0;

  parsed.rows.forEach((obj, i) => {
    for (const k of Object.keys(obj)) if (!RECOGNIZED_KEYS.has(k)) ignoredKeys.add(k);

    const missing: string[] = [];
    const name = typeof obj.name === "string" ? obj.name.trim() : "";
    if (!name) missing.push("name");
    const category = typeof obj.category === "string" ? obj.category.trim() : "";
    if (!category) missing.push("category");
    if (missing.length > 0) {
      skipped.push({ idx: i, missing });
      return;
    }

    const unitRaw = typeof obj.unit === "string" ? obj.unit.trim() : "";
    const unit = unitRaw || "each";
    const shippingUnit = typeof obj.shipping_unit === "string" ? obj.shipping_unit.trim() : "";
    const swParsed = parseNumber(obj.shipping_unit_weight);
    const shippingWeight = Number.isFinite(swParsed) && swParsed >= 0 ? swParsed : 0;
    const piecesParsed = parseNumber(obj.pieces_per_shipping_unit);
    let piecesPer =
      Number.isFinite(piecesParsed) && piecesParsed > 0 ? piecesParsed : 1;
    if (!shippingUnit || shippingUnit.toLowerCase() === unit.toLowerCase()) {
      piecesPer = 1;
    }

    const codeRaw = typeof obj.code === "string" ? obj.code.trim() : "";
    const qtyN = parseNumber(obj.qty);
    const qty = Number.isFinite(qtyN) && qtyN > 0 ? qtyN : undefined;
    const priceN = parseNumber(obj.price);
    const price = Number.isFinite(priceN) && priceN >= 0 ? priceN : undefined;
    const vendor =
      typeof obj.vendor === "string" && obj.vendor.trim() ? obj.vendor.trim() : undefined;
    const notes =
      typeof obj.notes === "string" && obj.notes.trim() ? obj.notes.trim() : undefined;
    const is_active = parseBoolean(obj.is_active);

    // The server only seeds cost entries on the CREATE path; rows that hit
    // an update don't disturb existing inventory. Match that here so the
    // preview's seeded count is accurate.
    const existing = nameIndex.get(normalizeName(name));
    const willHitUpdate = !!existing;
    const seedsCostEntry = !willHitUpdate && qty !== undefined && price !== undefined;
    if (seedsCostEntry) seededCostEntries += 1;

    const row: ParsedPreviewRow = {
      idx: i,
      name,
      unit,
      category,
      shipping_unit: shippingUnit,
      shipping_unit_weight: shippingWeight,
      pieces_per_shipping_unit: piecesPer,
      code: codeRaw || undefined,
      qty,
      price,
      vendor,
      notes,
      is_active,
      seedsCostEntry,
    };
    validRows.push(row);
    if (existing) willUpdate.push({ idx: i, row, existing });
    else willCreate.push(row);
  });

  return {
    kind: "ok",
    format: parsed.format,
    total: parsed.rows.length,
    validRows,
    skipped,
    ignoredKeys: Array.from(ignoredKeys).sort(),
    seededCostEntries,
    willUpdate,
    willCreate,
  };
}
